/**
 * ROBOT ANALİZ SAYFASI — İçerik Robotu'ndaki analiz kartına tıklanınca açılır:
 * o çalıştırmanın ürettiği brief kartları burada listelenir; kart → brief detayı.
 */
import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GeriButon } from "@/components/geri-buton";
import { useTakvimdeVar } from "@/hooks/use-takvim";
import { HacimRozeti } from "@/components/hacim-rozeti";
import { ArrowLeft, Target, Trophy, Check, CalendarPlus, Search } from "lucide-react";

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c", blue: "#ec6461" };
const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => (u || "").replace(/^https?:\/\/[^/]+/, "").replace(/\/$/, "") || "/";

const MARKETS: Record<string, string> = {
  auto: "🌐 Otomatik", tr: "🇹🇷 Türkçe", en: "🇺🇸 İngilizce (US)", "en-gb": "🇬🇧 İngilizce (UK)", de: "🇩🇪 Almanca",
  fr: "🇫🇷 Fransızca", es: "🇪🇸 İspanyolca", it: "🇮🇹 İtalyanca", ar: "🇸🇦 Arapça", ru: "🇷🇺 Rusça",
  nl: "🇳🇱 Felemenkçe", pt: "🇵🇹 Portekizce", "pt-br": "🇧🇷 Portekizce (BR)", pl: "🇵🇱 Lehçe", sv: "🇸🇪 İsveççe",
  el: "🇬🇷 Yunanca", he: "🇮🇱 İbranice", ja: "🇯🇵 Japonca", ko: "🇰🇷 Korece", zh: "🇹🇼 Çince", hi: "🇮🇳 Hintçe", id: "🇮🇩 Endonezce", az: "🇦🇿 Azerice",
};
const feasStyle: Record<string, { c: string; l: string }> = { kolay: { c: "#12b76a", l: "Kolay" }, orta: { c: "#f79009", l: "Orta" }, "iddialı": { c: "#f04438", l: "İddialı" } };
const prioStyle: Record<string, { c: string; l: string }> = { high: { c: "#f04438", l: "Yüksek" }, medium: { c: "#f79009", l: "Orta" }, low: { c: "#a3a3a0", l: "Düşük" } };

type Brief = {
  title: string; targetKeyword: string; volume: number; globalVolume?: number; difficulty: number;
  supportingKeywords: string[]; clusterName: string | null; isNewCluster?: boolean;
  contentKind?: string; intentKind?: string; priority: string; feasibility: string;
  rationale: string; outline: { h2: string; h3: string[] }[];
  competitorEvidence?: { url: string; traffic: number } | null;
  cannibalizationCheck?: { verdict: string; note: string; closestExisting: string | null } | null;
};
type PlannerResp = {
  generatedAt: string | null;
  runId?: number | null;
  runs?: { id: number; runAt: string; briefCount: number }[];
  data: {
    briefs: Brief[]; siteStrength: { organicTraffic: number; organicKeywords: number }; meta: { market: string; mode?: string; topic?: string };
    opportunityKeywords?: { keyword: string; volume: number; difficulty: number; cpc?: number; source: string; usedInBrief: boolean }[];
  } | null;
};

export default function PlannerAnalysis() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const queryClient = useQueryClient();
  // Yerel state YALNIZ o anki tıklama için; kalıcı durum sunucudan okunur.
  const [added, setAdded] = useState<Record<string, boolean>>({});
  const takvimdeVar = useTakvimdeVar(activeDomainId);
  const eklendi = (kw: string) => added[kw] || takvimdeVar(kw);
  const [sent, setSent] = useState<Record<string, boolean>>({});
  // Koşu geçmişi: null = en yeni; eski koşular tarih tarih seçilip açılır
  const [runId, setRunId] = useState<number | null>(null);

  const { data: planner } = useQuery({
    queryKey: ["content-planner", activeDomainId, runId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<PlannerResp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/content-planner${runId ? `?runId=${runId}` : ""}`); return r.ok ? await r.json() : { generatedAt: null, data: null }; } catch { return { generatedAt: null, data: null }; }
    },
  });
  const d = planner?.data ?? null;

  const removeBrief = useMutation({
    mutationFn: async (targetKeyword: string) => {
      const r = await fetch(`/api/domains/${activeDomainId}/content-planner/brief`, { method: "DELETE", headers: { "content-type": "application/json" }, body: JSON.stringify({ targetKeyword }) });
      if (!r.ok) throw new Error("Silinemedi");
      return r.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["content-planner", activeDomainId] }),
  });
  const addToCalendar = async (b: Brief) => {
    if (!activeDomainId) return;
    const outlineTxt = b.outline.map((o) => `## ${o.h2}${o.h3.length ? "\n" + o.h3.map((h) => `  - ${h}`).join("\n") : ""}`).join("\n");
    await fetch(`/api/domains/${activeDomainId}/content-plans`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ title: b.title, plannedDate: new Date().toISOString().slice(0, 10), contentType: "blog", notes: `${b.rationale}\n\n${outlineTxt}`, keywords: [{ keyword: b.targetKeyword, usa: 0, global: b.volume, role: "ana" }, ...b.supportingKeywords.map((k) => ({ keyword: k, usa: 0, global: 0, role: "destek" }))] }),
    });
    setAdded((p) => ({ ...p, [b.targetKeyword]: true }));
    queryClient.invalidateQueries({ queryKey: ["content-plans", activeDomainId] });
  };
  const sendToWriter = async (b: Brief) => {
    if (!activeDomainId) return;
    const r = await fetch(`/api/domains/${activeDomainId}/writer-briefs`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ brief: b }) });
    if (r.ok || r.status === 409) setSent((p) => ({ ...p, [b.targetKeyword]: true }));
  };

  return (
    <div className="px-4 sm:px-6 py-6 w-full">
      <GeriButon yedekHref="/content-planner" yedekEtiket="Brief Keşif Ajanı" className="mb-3" />

      {!d && <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Analiz bulunamadı — Brief Keşif Ajanı'ndan çalıştır.</CardContent></Card>}

      {/* KOŞU GEÇMİŞİ — tüm plan koşuları tarih tarih; eski brief'ler kaybolmaz */}
      {(planner?.runs?.length ?? 0) > 1 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {(planner?.runs ?? []).map((r) => {
            const active = (planner?.runId ?? null) === r.id;
            return (
              <button key={r.id} onClick={() => setRunId(r.id)}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition ${active ? "" : "bg-white border-black/10 text-[#70706c] dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"}`}
                style={active ? { background: "rgba(139,92,246,0.1)", borderColor: "#e83e3c", color: "#d42b29" } : undefined}>
                {new Date(r.runAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })} {new Date(r.runAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })} · {r.briefCount} brief
              </button>
            );
          })}
        </div>
      )}

      {d && (<>
        <h1 className="text-xl font-bold">📊 {planner?.generatedAt ? new Date(planner.generatedAt).toLocaleDateString("tr-TR", { dateStyle: "long" }) : ""} Analizi</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-5">
          {MARKETS[d.meta.market] ?? d.meta.market.toUpperCase()}{d.meta.mode === "topic" && d.meta.topic ? ` · "${d.meta.topic}"` : ""} · <b className="text-foreground">{d.briefs.length} brief</b> · site gücü {fmt(d.siteStrength.organicTraffic)}/ay · {fmt(d.siteStrength.organicKeywords)} sıralı kelime
        </p>

        {/* KOMPAKT LİSTE (kullanıcı isteği): ağır rozetli kartlar yerine Brief'ler
            listesiyle aynı sade satır düzeni — detay/rasyonel brief sayfasında. */}
        <Card><CardContent className="p-4">
          <div className="divide-y divide-border">
            {d.briefs.map((b) => {
              const prio = prioStyle[b.priority] ?? prioStyle.medium;
              return (
                <div key={b.targetKeyword} className="flex items-center gap-3 py-2.5">
                  <span title={`${prio.l} öncelik`} className="h-2 w-2 shrink-0 rounded-full" style={{ background: prio.c }} />
                  <Link href={`/planner-brief/${encodeURIComponent(b.targetKeyword)}`} className="min-w-0 flex-1 cursor-pointer hover:opacity-75 transition">
                    <div className="text-sm font-medium truncate">{b.title}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {b.targetKeyword}
                      {b.volume > 0 ? ` · ${fmt(b.volume)}/ay` : b.globalVolume ? ` · dünya ${fmt(b.globalVolume)}/ay` : " · hacim yok"}
                      {b.difficulty > 0 ? ` · zorluk ${b.difficulty}` : ""}
                      {b.clusterName ? ` · ${b.isNewCluster ? "🆕 " : ""}${b.clusterName}` : ""}
                      {b.intentKind ? ` · ${b.intentKind === "ticari" ? "ticari" : "bilgi"}` : ""}
                    </div>
                  </Link>
                  <div className="flex shrink-0 items-center gap-1">
                    <button title={eklendi(b.targetKeyword) ? "Takvime eklendi" : "İçerik Takvimine Ekle"} disabled={eklendi(b.targetKeyword)}
                      onClick={() => void addToCalendar(b)}
                      className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-black/[0.04] hover:text-foreground disabled:opacity-40 dark:hover:bg-white/[0.06]">
                      {eklendi(b.targetKeyword) ? <Check className="h-4 w-4" style={{ color: C.green }} /> : <CalendarPlus className="h-4 w-4" />}
                    </button>
                    <button title={sent[b.targetKeyword] ? "Üretim Ajanı havuzunda" : "Üretim Ajanına Gönder"} disabled={sent[b.targetKeyword]}
                      onClick={() => void sendToWriter(b)}
                      className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-black/[0.04] hover:text-foreground disabled:opacity-40 dark:hover:bg-white/[0.06]">
                      {sent[b.targetKeyword] ? <Check className="h-4 w-4" style={{ color: C.green }} /> : <span className="text-[13px] leading-none">✍️</span>}
                    </button>
                    <button title="Brief'i sil" disabled={removeBrief.isPending}
                      onClick={() => { if (window.confirm(`"${b.targetKeyword}" önerisi silinsin mi?`)) removeBrief.mutate(b.targetKeyword); }}
                      className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-black/[0.04] hover:text-red-500 disabled:opacity-40 dark:hover:bg-white/[0.06]">
                      <span className="text-[13px] leading-none">🗑</span>
                    </button>
                  </div>
                </div>
              );
            })}
            {d.briefs.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Bu analizde brief kalmadı.</div>}
          </div>
        </CardContent></Card>

        {/* ── FIRSAT KELİME TABLOSU — bu analizde keşfedilen tüm adaylar (kullanıcı isteğiyle buraya taşındı) ── */}
        {(d.opportunityKeywords?.length ?? 0) > 0 && (
          <Card className="mt-5"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2.5"><Search className="w-4 h-4" style={{ color: C.amber }} /><span className="font-semibold text-sm">Fırsat Kelime Tablosu</span><span className="text-[11px] text-muted-foreground">({d.opportunityKeywords!.length} aday · hepsi sitede hedeflenmemiş)</span></div>
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="sticky top-0 bg-white dark:bg-gray-900"><tr className="text-[11px] text-muted-foreground text-left border-b" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
                  <th className="py-1.5">Kelime</th><th className="py-1.5 text-right">Aylık Hacim</th><th className="py-1.5 text-right">Zorluk</th><th className="py-1.5 text-right">CPC</th><th className="py-1.5">Kaynak</th><th className="py-1.5">Brief</th>
                </tr></thead>
                <tbody>{d.opportunityKeywords!.map((k, i) => {
                  const srcColor: Record<string, string> = { "rakip-gap": C.red, "rakip-sayfa": "#fb6514", "ilişkili": C.cyan, "gsc-fırsat": C.blue, "cluster-önerisi": C.purple, "konu": "#d42b29" };
                  const diffColor = k.difficulty >= 60 ? C.red : k.difficulty >= 30 ? C.amber : C.green;
                  return (
                    <tr key={i} className="border-b last:border-0" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
                      <td className="py-1.5 pr-3 font-medium">{k.keyword}</td>
                      <td className="py-1.5 text-right tabular-nums" style={{ color: C.green }}>{fmt(k.volume)}</td>
                      <td className="py-1.5 text-right tabular-nums" style={{ color: k.difficulty > 0 ? diffColor : "#70706c" }}>{k.difficulty > 0 ? k.difficulty : "—"}</td>
                      <td className="py-1.5 text-right tabular-nums" style={{ color: (k.cpc ?? 0) > 0 ? "#d42b29" : "#a3a3a0" }}>{(k.cpc ?? 0) > 0 ? `$${k.cpc!.toFixed(2)}` : "—"}</td>
                      <td className="py-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${srcColor[k.source] ?? "#70706c"}22`, color: srcColor[k.source] ?? "#a3a3a0" }}>{k.source}</span></td>
                      <td className="py-1.5">{k.usedInBrief ? <span className="text-[10px]" style={{ color: C.green }}>✓ brief'te</span> : <span className="text-[10px] text-muted-foreground">—</span>}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </CardContent></Card>
        )}
      </>)}
    </div>
  );
}
